import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useAppServices } from "../../../../../../../hook/services";
import { IconsArray } from "../../../../../constants/Icons";
import { toast } from "react-toastify";
import { MMStyles } from "../AddNewMenuModal";
import { MenuHideExtras } from "../MenuHideModal";

const MenuEditsModal = ({ data, selectedItem, isDisabled, handleRefresh }) => {
  const AppService = useAppServices();
  const { companyId, type } = useParams();
  const handler =
    type === "agency" || type === "location"
      ? AppService.sidenav_customizer_main_level
      : type === "group"
        ? AppService.sidenav_customizer_custom_level
        : AppService.sidenav_customizer_saas_level;

  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [editedLabels, setEditedLabels] = useState([]);
  const [iconSearch, setIconSearch] = useState("");
  const [showIconSelector, setShowIconSelector] = useState(false);
  const [selectedIcon, setSelectedIcon] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (data?.menus) {
      setEditedLabels(data.menus);
    }
  }, [data]);

  useEffect(() => {
    if (!selectedItem) return;
    setLabel(selectedItem?.newLabel || selectedItem.label || "");

    if (selectedItem?.icon) {
      if (selectedItem?.isCustom) {
        const iconSplit = selectedItem.icon.split(" ");
        const iconName = iconSplit[1]?.split("-")[1];
        const matchedIcon = IconsArray.find((i) => i.name === iconName);
        setSelectedIcon(matchedIcon || null);
      } else {
        const unicodeMatch = selectedItem.icon.match(/&#x([a-fA-F0-9]+);/);
        const unicode = unicodeMatch?.[1];
        const matchedIcon = IconsArray.find((i) => i.unicode === unicode);
        setSelectedIcon(matchedIcon || null);
      }
    } else {
      setSelectedIcon(null);
    }
  }, [selectedItem]);

  const resetState = () => {
    if (submitting) return;
    setOpen(false);
    setLabel("");
    setSelectedIcon(null);
    setShowIconSelector(false);
    setIconSearch("");
  };

  const handleSave = async () => {
    if (!selectedItem || !label.trim()) {
      toast.warning("Menu label cannot be empty.");
      return;
    }
    setSubmitting(true);

    const iconHtml = selectedIcon ? `&#x${selectedIcon.unicode};` : "";

    const updatedItem = {
      icon: iconHtml,
      newLabel: label.trim(),
      type: "edit",
      selector: selectedItem.selector,
    };

    const updatedEditMenus = [...editedLabels];

    let existingIndex = updatedEditMenus.findIndex(
      (edit) => edit.selector === selectedItem.selector,
    );

    if (existingIndex !== -1) {
      const defaultIcon = selectedItem?.icon;
      const defaultLabel =
        defaultIcon?.split(" ")[1]?.split("-").slice(1).join("-") || "yin-yang";
      const defaultIconFontFamily = defaultIcon?.split(" ")[0] || "fas";
      const customMenuLinkResponse =
        await AppService.webhook.update_ghl_custom_menu_link({
          payload: {
            companyId,
            customMenuLinkId: selectedItem.selector,
            customMenuLinkTitle: label.trim(),
            icon: {
              name: selectedIcon ? selectedIcon.name : defaultLabel,
              fontFamily: selectedIcon
                ? selectedIcon.fontFamily
                : defaultIconFontFamily,
            },
          },
        });

      updatedEditMenus[existingIndex] = {
        ...updatedEditMenus[existingIndex],
        ...updatedItem,
      };
    } else {
      const parentIndex = updatedEditMenus.findIndex(
        (item) =>
          Array.isArray(item.children) &&
          item.children.some(
            (child) => child.selector === selectedItem.selector,
          ),
      );

      if (parentIndex !== -1) {
        const childIndex = updatedEditMenus[parentIndex].children.findIndex(
          (child) => child.selector === selectedItem.selector,
        );

        if (childIndex !== -1) {
          updatedEditMenus[parentIndex].children[childIndex] = {
            ...updatedEditMenus[parentIndex].children[childIndex],
            ...updatedItem,
          };
        }
      }
    }

    const payload = {
      companyId,
      menus: updatedEditMenus,
      type,
      ...(data?._id && { _id: data._id }),
    };

    try {
      const { response } = await handler[data?._id ? "update" : "create"]({
        payload,
      });

      if (response) {
        setEditedLabels(updatedEditMenus);
        setOpen(false);
        setLabel("");
        setSelectedIcon(null);
        setShowIconSelector(false);
        setIconSearch("");
        handleRefresh();
        toast.success("Menu updated successfully.");
      } else {
        toast.error("Failed to save menu changes.");
      }
    } catch (error) {
      console.error("Failed to save menu edit:", error);
      toast.error("An error occurred while saving. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const filteredIcons = IconsArray.filter((icon) =>
    icon.name.toLowerCase().includes(iconSearch.toLowerCase()),
  );

  return (
    <>
      <button
        type="button"
        className="mm-icon-btn"
        title="Edit menu"
        onClick={() => setOpen(true)}
        disabled={isDisabled}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
        </svg>
      </button>

      {open && (
        <div className="mm-overlay" onMouseDown={resetState}>
          <div
            className="mm-dialog"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => e.stopPropagation()}
            style={{ maxWidth: 560 }}
          >
            <header className="mm-header d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mm-title mb-0">Edit menu</h5>
                <p className="mm-subtitle mb-0">
                  Update the label or icon for <span style={{ color: "#1f2937", fontWeight: 500 }}>{selectedItem?.label}</span>.
                </p>
              </div>
              <button type="button" className="mm-close" onClick={resetState} aria-label="Close">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </header>

            <div className="mm-body">
              <div className="mm-field">
                <label className="mm-label">Menu label</label>
                <input
                  className="mm-input"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="Enter a label"
                />
              </div>

              <div className="mm-field mb-0">
                <label className="mm-label">Icon</label>
                <div className="mm-icon-input-wrap">
                  {selectedIcon && (
                    <span className="mm-icon-preview">
                      <i className={`fas fa-${selectedIcon.name}`}></i>
                    </span>
                  )}
                  <input
                    className="mm-input mm-input--with-prefix"
                    placeholder="Search icon"
                    value={showIconSelector ? iconSearch : selectedIcon?.name || ""}
                    onClick={() => setShowIconSelector((prev) => !prev)}
                    onChange={(e) => setIconSearch(e.target.value)}
                    readOnly={!showIconSelector}
                    style={selectedIcon ? { paddingLeft: 44 } : {}}
                  />
                  <button
                    type="button"
                    className="mm-input-action"
                    onClick={() => setShowIconSelector((prev) => !prev)}
                    aria-label="Toggle icon picker"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                </div>

                {showIconSelector && (
                  <div className="mm-icon-grid">
                    {filteredIcons.length === 0 ? (
                      <p className="mm-icon-empty">No icons found.</p>
                    ) : (
                      <div className="mm-icon-grid-inner">
                        {filteredIcons.map((icon, i) => (
                          <button
                            key={i}
                            type="button"
                            className={`mm-icon-cell ${selectedIcon?.name === icon.name ? "is-selected" : ""}`}
                            onClick={() => {
                              setSelectedIcon(icon);
                              setShowIconSelector(false);
                            }}
                            title={icon.name}
                          >
                            <i className={`fas fa-${icon.name}`}></i>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <footer className="mm-footer d-flex justify-content-end gap-2">
              <button type="button" className="mm-btn mm-btn--ghost" onClick={resetState} disabled={submitting}>
                Cancel
              </button>
              <button type="button" className="mm-btn mm-btn--primary" onClick={handleSave} disabled={submitting}>
                {submitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                    Saving
                  </>
                ) : (
                  "Save changes"
                )}
              </button>
            </footer>
          </div>
        </div>
      )}

      <MMStyles />
      <MenuHideExtras />
    </>
  );
};

export default MenuEditsModal;
